/**
 * Pharmacy payouts.
 *
 * Three tabs over the same money:
 *   Requests    withdrawals raised by pharmacies. Approving one is the ONLY
 *               thing in the system that sends money, and it is PIN-gated.
 *   Wallets     every pharmacy's live balance, credited automatically when an
 *               order is delivered.
 *   Commission  what Medstown keeps, per product type, plus the pharmacies on
 *               negotiated rates.
 *
 * Built on the shared kit rather than a private palette — the tokens file
 * exists because this page's kind of local `T` object had drifted across
 * eighteen screens.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, Button, Tabs, Tab, Divider } from "@mui/material";
import { MdRefresh, MdWarning } from "react-icons/md";
import API from "Config/API";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Surface, Kpi, StatusChip, DataTable, FilterBar } from "components/ui";
import { T, tint } from "theme/tokens";

import {
  ApproveDialog,
  RejectDialog,
  MarkPaidDialog,
  AdjustWalletDialog,
  money,
} from "./PayoutActionDialogs";
import PayoutDetailDialog from "./PayoutDetailDialog";
import CommissionRates from "./CommissionRates";
import PharmacyCommissionDialog from "./PharmacyCommissionDialog";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "processing,approved", label: "In progress" },
  { value: "paid", label: "Paid" },
  { value: "rejected,failed,cancelled", label: "Closed" },
  { value: "all", label: "All" },
];

const when = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

/** Two lines in one cell: what it is, and how to find it. */
function TwoLine({ primary, secondary }) {
  return (
    <Box sx={{ py: 0.6, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.83rem", fontWeight: 600, color: T.ink, lineHeight: 1.3 }} noWrap>
        {primary || "—"}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: T.subtle, fontFamily: "monospace" }} noWrap>
        {secondary || "—"}
      </Typography>
    </Box>
  );
}

const Money = ({ value, tone = T.ink, bold = 700 }) => (
  <Typography sx={{ fontSize: "0.85rem", fontWeight: bold, color: tone, fontVariantNumeric: "tabular-nums" }}>
    {money(value)}
  </Typography>
);

export default function PharmacyPayouts() {
  const [tab, setTab] = useState(0);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [gatewayReady, setGatewayReady] = useState(false);
  const [ledger, setLedger] = useState({ healthy: true, drift: null });

  // ── requests ──
  const [requests, setRequests] = useState([]);
  const [reqTotal, setReqTotal] = useState(0);
  const [reqLoading, setReqLoading] = useState(true);
  const [reqError, setReqError] = useState(null);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [reqPage, setReqPage] = useState({ page: 0, pageSize: 20 });

  // ── wallets ──
  const [wallets, setWallets] = useState([]);
  const [walletTotal, setWalletTotal] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [walletSearch, setWalletSearch] = useState("");
  const [walletPage, setWalletPage] = useState({ page: 0, pageSize: 20 });

  const [dialog, setDialog] = useState({ kind: null });

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await API.get("/admin/payouts/stats");
      setStats(data?.stats || null);
      setGatewayReady(!!data?.payoutGatewayReady);
      setLedger({ healthy: data?.ledgerHealthy !== false, drift: data?.ledgerDrift || null });
    } catch {
      /* the KPI row degrades to dashes; the tables report their own errors */
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setReqLoading(true);
    setReqError(null);
    try {
      const { data } = await API.get("/admin/payouts", {
        params: {
          status,
          search: search.trim() || undefined,
          page: reqPage.page + 1,
          limit: reqPage.pageSize,
        },
      });
      setRequests(data?.list || []);
      setReqTotal(data?.pagination?.totalCount || 0);
      if (typeof data?.payoutGatewayReady === "boolean") setGatewayReady(data.payoutGatewayReady);
    } catch (err) {
      setReqError(err?.response?.data?.message || "Could not load payout requests.");
      setRequests([]);
    } finally {
      setReqLoading(false);
    }
  }, [status, search, reqPage]);

  const loadWallets = useCallback(async () => {
    setWalletLoading(true);
    setWalletError(null);
    try {
      const { data } = await API.get("/admin/pharmacy-wallets", {
        params: {
          search: walletSearch.trim() || undefined,
          page: walletPage.page + 1,
          limit: walletPage.pageSize,
        },
      });
      setWallets(data?.list || []);
      setWalletTotal(data?.pagination?.totalCount || 0);
    } catch (err) {
      setWalletError(err?.response?.data?.message || "Could not load pharmacy wallets.");
      setWallets([]);
    } finally {
      setWalletLoading(false);
    }
  }, [walletSearch, walletPage]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    const t = setTimeout(loadRequests, 250);
    return () => clearTimeout(t);
  }, [loadRequests]);

  useEffect(() => {
    if (tab !== 1) return;
    const t = setTimeout(loadWallets, 250);
    return () => clearTimeout(t);
  }, [tab, loadWallets]);

  const refreshAll = () => {
    loadStats();
    loadRequests();
    if (tab === 1) loadWallets();
  };

  // ── columns ────────────────────────────────────────────────────────────
  const requestColumns = useMemo(
    () => [
      {
        field: "pharmacyName",
        headerName: "Pharmacy",
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => (
          <TwoLine
            primary={row.pharmacyName || "Unnamed pharmacy"}
            secondary={`${row.pharmacyId} · ${row.pharmacyPhone || "no phone"}`}
          />
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <Money value={row.amount} />,
      },
      {
        field: "bank",
        headerName: "Bank account",
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.4} sx={{ py: 0.5, minWidth: 0 }}>
            <TwoLine
              primary={row.bankDetails?.bankName || "—"}
              secondary={`${row.bankDetails?.accountNumber || "—"} · ${row.bankDetails?.ifscCode || "—"}`}
            />
            {row.bankVerified === false && (
              <StatusChip status="pending" label="Unverified" />
            )}
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.3} sx={{ py: 0.6 }}>
            <StatusChip status={row.status} />
            {row.razorpay?.status && (
              <Typography sx={{ fontSize: "0.62rem", color: T.subtle }}>rzp: {row.razorpay.status}</Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "requestedAt",
        headerName: "Requested",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography sx={{ fontSize: "0.75rem", color: T.muted }}>
            {when(row.requestedAt || row.createdAt)}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 190,
        sortable: false,
        renderCell: ({ row }) => {
          // A request Razorpay already holds cannot be rejected here — only
          // Razorpay can end it, via webhook or sync.
          const canReject =
            ["pending", "approved"].includes(row.status) ||
            (row.status === "processing" && !row.razorpay?.payoutId);
          return (
            <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
              {row.status === "pending" && (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  onClick={() => setDialog({ kind: "approve", request: row })}
                  sx={{ bgcolor: T.success, fontSize: "0.7rem", py: 0.3, "&:hover": { bgcolor: "#3d9140" } }}
                >
                  Approve
                </Button>
              )}
              {canReject && (
                <Button
                  size="small"
                  onClick={() => setDialog({ kind: "reject", request: row })}
                  sx={{ fontSize: "0.7rem", color: T.error }}
                >
                  Reject
                </Button>
              )}
              {["pending", "approved", "processing", "failed"].includes(row.status) && (
                <Button
                  size="small"
                  onClick={() => setDialog({ kind: "markPaid", request: row })}
                  sx={{ fontSize: "0.7rem", color: T.muted }}
                >
                  Mark paid
                </Button>
              )}
            </Stack>
          );
        },
      },
    ],
    []
  );

  const walletColumns = useMemo(
    () => [
      {
        field: "pharmacyName",
        headerName: "Pharmacy",
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => (
          <TwoLine primary={row.pharmacyName || "Unnamed pharmacy"} secondary={`${row.pharmacyId} · ${row.phone || "no phone"}`} />
        ),
      },
      {
        field: "balance",
        headerName: "Withdrawable",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => <Money value={row.balance} tone={T.success} />,
      },
      {
        field: "locked",
        headerName: "Locked",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => <Money value={row.locked} tone={T.warning} bold={600} />,
      },
      {
        field: "lifetimeCredited",
        headerName: "Earned",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <Money value={row.lifetimeCredited} tone={T.muted} bold={500} />,
      },
      {
        field: "lifetimeWithdrawn",
        headerName: "Paid out",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <Money value={row.lifetimeWithdrawn} tone={T.muted} bold={500} />,
      },
      {
        field: "bank",
        headerName: "Bank",
        width: 150,
        sortable: false,
        renderCell: ({ row }) =>
          row.bankLinked ? (
            <Typography sx={{ fontSize: "0.76rem", color: T.ink }}>
              {row.bankName || "Bank"} ····{row.bankAccountLast4}
            </Typography>
          ) : (
            <StatusChip status="pending" label="No bank" />
          ),
      },
      {
        field: "rates",
        headerName: "Rates",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <StatusChip
            status={row.onCustomRates ? "confirmed" : "active"}
            label={row.onCustomRates ? "Negotiated" : "Standard"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 170,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
            <Button size="small" onClick={() => setDialog({ kind: "commission", wallet: row })} sx={{ fontSize: "0.7rem", color: T.secondary }}>
              Rates
            </Button>
            <Button size="small" onClick={() => setDialog({ kind: "adjust", wallet: row })} sx={{ fontSize: "0.7rem", color: T.primary }}>
              Adjust
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <ToastContainer position="bottom-right" autoClose={2500} />

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Surface sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
          <Box>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: T.primary, letterSpacing: "-0.02em" }}>
              Pharmacy Payouts
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: T.muted, mt: 0.3, maxWidth: 620 }}>
              Earnings are credited automatically when an order is delivered. Approving a
              withdrawal is the only action here that sends money.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusChip
              status={gatewayReady ? "active" : "pending"}
              label={gatewayReady ? "RazorpayX connected" : "Manual transfers only"}
            />
            <Button size="small" startIcon={<MdRefresh />} onClick={refreshAll} sx={{ color: T.primary }}>
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Surface>

      {/* A ledger that does not balance means money was created or destroyed.
          It belongs at the top of the page, not in a log. */}
      {!ledger.healthy && (
        <Surface sx={{ mb: 2, p: 2, borderColor: tint(T.error, 0.4), bgcolor: tint(T.error, 0.06) }}>
          <Stack direction="row" spacing={1.2} alignItems="flex-start">
            <MdWarning size={20} color={T.error} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: T.error, fontSize: "0.9rem" }}>
                Ledger does not balance
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: T.ink, mt: 0.3 }}>
                Every account should sum to zero and currently sums to <b>{money(ledger.drift)}</b>. Stop
                approving payouts and run <code>node scripts/reconcileLedger.js --deep</code>.
              </Typography>
            </Box>
          </Stack>
        </Surface>
      )}

      {/* ── KPIs ───────────────────────────────────────────────────────── */}
      <Surface sx={{ mb: 2 }}>
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} flexWrap="wrap">
          <Kpi label="Awaiting approval" value={money(stats?.pendingAmount)} sub={`${stats?.pendingCount || 0} request(s)`} tone={T.warning} loading={statsLoading} sx={{ flex: "1 1 170px" }} />
          <Kpi label="In progress" value={money(stats?.processingAmount)} sub={`${stats?.processingCount || 0} sending`} tone={T.info} loading={statsLoading} sx={{ flex: "1 1 170px" }} />
          <Kpi label="Paid out" value={money(stats?.paidAmount)} sub={`${stats?.paidCount || 0} settled`} tone={T.success} loading={statsLoading} sx={{ flex: "1 1 170px" }} />
          <Kpi label="Wallet balance" value={money(stats?.totalWalletBalance)} sub={`${stats?.wallets || 0} pharmacies`} tone={T.primary} loading={statsLoading} sx={{ flex: "1 1 170px" }} />
          <Kpi label="Locked" value={money(stats?.totalLockedBalance)} sub="held for open requests" loading={statsLoading} sx={{ flex: "1 1 170px" }} />
        </Stack>
      </Surface>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Surface sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 1,
            borderBottom: `1px solid ${T.borderSoft}`,
            "& .MuiTab-root": { fontSize: "0.85rem", fontWeight: 600, textTransform: "none", minHeight: 46 },
            "& .Mui-selected": { color: `${T.primary} !important` },
            "& .MuiTabs-indicator": { backgroundColor: T.secondary, height: 3 },
          }}
        >
          <Tab label={`Requests${stats?.pendingCount ? ` (${stats.pendingCount})` : ""}`} />
          <Tab label="Wallets" />
          <Tab label="Commission" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <FilterBar
              search={search}
              onSearchChange={(v) => { setSearch(v); setReqPage((p) => ({ ...p, page: 0 })); }}
              searchPlaceholder="Pharmacy, request ID or UTR…"
              selects={[
                {
                  key: "status",
                  label: "Status",
                  value: status,
                  options: STATUS_OPTIONS,
                  onChange: (v) => { setStatus(v); setReqPage((p) => ({ ...p, page: 0 })); },
                },
              ]}
              onReset={() => { setSearch(""); setStatus("pending"); setReqPage({ page: 0, pageSize: 20 }); }}
              onRefresh={refreshAll}
            />
            <Box sx={{ mt: 1.5 }}>
              <DataTable
                rows={requests}
                columns={requestColumns}
                loading={reqLoading}
                error={reqError}
                onRetry={loadRequests}
                emptyMessage="No withdrawal requests here."
                rowCount={reqTotal}
                paginationModel={reqPage}
                onPaginationModelChange={setReqPage}
                getRowId={(r) => r.requestId}
                onRowClick={({ row }) => setDialog({ kind: "detail", requestId: row.requestId })}
                rowHeight={62}
              />
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <FilterBar
              search={walletSearch}
              onSearchChange={(v) => { setWalletSearch(v); setWalletPage((p) => ({ ...p, page: 0 })); }}
              searchPlaceholder="Pharmacy name, ID or phone…"
              onReset={() => { setWalletSearch(""); setWalletPage({ page: 0, pageSize: 20 }); }}
              onRefresh={loadWallets}
            />
            <Box sx={{ mt: 1.5 }}>
              <DataTable
                rows={wallets}
                columns={walletColumns}
                loading={walletLoading}
                error={walletError}
                onRetry={loadWallets}
                emptyMessage="No pharmacy wallets yet — they are created on the first delivered order."
                rowCount={walletTotal}
                paginationModel={walletPage}
                onPaginationModelChange={setWalletPage}
                getRowId={(r) => r.pharmacyId}
                rowHeight={58}
              />
            </Box>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <CommissionRates />
          </Box>
        )}
      </Surface>

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      <PayoutDetailDialog
        open={dialog.kind === "detail"}
        requestId={dialog.requestId}
        onClose={() => setDialog({ kind: null })}
        onDone={refreshAll}
      />
      <ApproveDialog open={dialog.kind === "approve"} request={dialog.request} gatewayReady={gatewayReady} onClose={() => setDialog({ kind: null })} onDone={refreshAll} />
      <RejectDialog open={dialog.kind === "reject"} request={dialog.request} onClose={() => setDialog({ kind: null })} onDone={refreshAll} />
      <MarkPaidDialog open={dialog.kind === "markPaid"} request={dialog.request} onClose={() => setDialog({ kind: null })} onDone={refreshAll} />
      <PharmacyCommissionDialog open={dialog.kind === "commission"} pharmacy={dialog.wallet} onClose={() => setDialog({ kind: null })} onDone={() => { loadWallets(); loadStats(); }} />
      <AdjustWalletDialog open={dialog.kind === "adjust"} wallet={dialog.wallet} onClose={() => setDialog({ kind: null })} onDone={() => { loadWallets(); loadStats(); }} />
    </Box>
  );
}
